
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

interface AssessmentHistory {
  id: string;
  session_id: string;
  questions_answers: Json;
  result: Json;
  completed_at: string;
}

interface HistoryListProps {
  histories: AssessmentHistory[];
  selectedHistory: AssessmentHistory | null;
  onSelectHistory: (history: AssessmentHistory) => void;
}

const HistoryList = ({ histories, selectedHistory, onSelectHistory }: HistoryListProps) => {
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "emergency": return "destructive";
      case "urgent": return "secondary";
      case "routine": return "default";
      case "self-care": return "outline";
      default: return "outline";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getResultData = (result: Json) => {
    if (typeof result === 'object' && result !== null && !Array.isArray(result)) {
      return result as { urgency?: string; category?: string; recommendation?: string };
    }
    return { urgency: 'unknown', category: 'Unknown', recommendation: 'No recommendation available' };
  };

  return (
    <Card className="p-6 bg-white/80 backdrop-blur-sm border-blue-100">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Assessments</h2>
      <div className="space-y-4">
        {histories.map((history) => {
          const resultData = getResultData(history.result);
          return (
            <div
              key={history.id}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                selectedHistory?.id === history.id 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-200 hover:bg-blue-25'
              }`}
              onClick={() => onSelectHistory(history)}
            >
              <div className="flex items-center justify-between mb-2">
                <Badge variant={getUrgencyColor(resultData.urgency || 'unknown')}>
                  {resultData.urgency || 'unknown'}
                </Badge>
                <span className="text-sm text-gray-500 flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  {formatDate(history.completed_at)}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900">{resultData.category || 'Unknown'}</p>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{resultData.recommendation || 'No recommendation'}</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default HistoryList;
